'use client';

import { isSafeReturnPath } from '@/lib/auth/return-path';
import { parseArrivalFragment, stashArrivalFragment } from '@/lib/auth/arrival-fragment';
import { adoptArrivalQuery } from '@/lib/auth/arrival-query';
import {
  ARRIVAL_FRAGMENT_APP_ID,
  conferenceFromFragment,
  inviterInitial,
  peekArrivalFragment,
  resolveLoginApp,
  signInPartnerName,
} from '@/lib/auth/login-app';
import {
  SIGN_IN_SCREEN_ROUTE,
  isConferenceFolderId,
  pickSignInText,
  screenFromWire,
  type PublishedSignInScreen,
  type SignInScreenWire,
} from '@/lib/auth/published-sign-in';
import { detectInAppBrowser, type InAppBrowserDetection } from '@/lib/auth/in-app-browser';
import { rememberTwaReferrer } from '@/lib/platform/twa-referrer';
import type { LoginBrand } from '@/lib/auth/login-brand';
import {
  SIGN_IN_PROVIDER_INFO,
  isSignInProvider,
  signInProvidersFor,
  type SignInProvider,
} from '@/lib/auth/sign-in-providers';
import { FLAGSHIP_BY_APP_ID, type FlagshipApp, type FlagshipSignInImage } from '@/lib/workspaces/flagship-apps';
import { coverGradient } from '@/lib/marketplace/cover';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { cn } from '@/lib/utils';
import { Sparkles, Fingerprint, ArrowRight, ChevronDown, UserRound } from 'lucide-react';
import { useState, useEffect, useMemo, useId, useRef } from 'react';
import { useTranslation } from '@/components/i18n-provider';
import { useAnalytics } from '@/hooks/use-analytics';
import { ProviderIcon } from '@/components/provider-icon';
import { RobutlerLogo } from '@/components/robutler-logo';
import { WidgetMark } from '@/components/workspace/panels/content-preview';
import { InAppBrowserSheet } from '@/components/auth/in-app-browser-sheet';
import { availableSignInProviders } from '@/components/auth/sign-in-providers-available';

/**
 * The line for a LINK somebody sent (round 6, 2026-09-27): a Revent share
 * link or the landing's hand-off carries an event or a conference in its
 * arrival fragment (`LoginApp.sentLink`), and the app's own line (the SF
 * Tech Week sentence) is the wrong event for everyone an organizer sends a
 * link to. The fragment's contents are still never drawn (lib/auth/login-app.ts).
 */
const APP_LINK_INTRO_KEY: Readonly<Record<string, string>> = {
  revent: 'auth.appIntroReventLink',
};
/** The same for a sent link that names a conference and no event (`LoginApp.sentConference`). */
const APP_CONFERENCE_LINK_INTRO_KEY: Readonly<Record<string, string>> = {
  revent: 'auth.appIntroReventConferenceLink',
};

/** How many of an app's providers are buttons before "Other ways to sign in" (decision 5, 2026-10-02). */
const APP_SCREEN_PRIMARY_COUNT = 2;
/** The longest event name the band draws, in code points (the lookup clamps to 90 and may add one mark). */
const SENT_EVENT_NAME_MAX = 100;
/** How long a screen without a live provider list waits for /api/auth/sign-in-providers. */
const SIGN_IN_PROVIDERS_WAIT_MS = 2500;

/**
 * What a project puts on the screen, from whichever source has it: the
 * conference's PUBLISHED screen (its words as text, its pictures from our own
 * route) or the project's CONSTANTS (translation keys and committed files).
 * The label under the mark and the partner table are always the constants'
 * (a published screen carries neither), so a conference whose data equals
 * its constants draws exactly what it drew before.
 */
type ProjectScreenView = {
  source: 'published' | 'constants';
  /** The project's own heading, as drawn; null: the app's. */
  heading: string | null;
  line: string;
  image: FlagshipSignInImage | null;
  nameKey: string | null;
  partners: Readonly<Record<string, string>> | null;
};

const hasOwn = (o: object, key: string) => Object.prototype.hasOwnProperty.call(o, key);

/** The published screens a page handed over, validated again here: only folder ids, only screens. */
function screensFromWire(
  wire: Readonly<Record<string, SignInScreenWire | null>> | null | undefined,
): Readonly<Record<string, PublishedSignInScreen | null>> {
  const out: Record<string, PublishedSignInScreen | null> = {};
  if (!wire || typeof wire !== 'object') return out;
  for (const [id, value] of Object.entries(wire)) {
    if (isConferenceFolderId(id)) out[id] = value ? screenFromWire(id, value) : null;
  }
  return out;
}

/**
 * The body of the login / signup screen, parameterised so it can
 * render both inside the workspace dialog stack (via
 * `?dialog=login` / `?dialog=signup`) AND as the standalone
 * `/login` SSR fallback page.
 *
 * Inputs come from `URLSearchParams` rather than `useSearchParams`
 * so the same component can mount inside the dialog (where the
 * caller passes its own params) or inside the page (which forwards
 * `useSearchParams()`).
 *
 * `mode` controls the heading + the analytics event. The body
 * itself is identical because the OAuth providers don't
 * distinguish "log in" from "sign up" at the start route — every
 * provider falls through to the same callback, and the callback
 * decides whether to create or attach the user. The flag is what
 * the post-auth `clone-on-signup` logic reads to know whether to
 * clone the source workspace into the freshly-minted account.
 *
 * `brand` (app-hosts plan D1 and D2, 2026-09-24) is the APP this screen
 * signs somebody in to, resolved by the server page from the verified
 * host table and never from the query. ON the host (`brand.onHost`) the
 * provider buttons hop through `/api/auth/domain/start`, a "Continue"
 * offers the guest hop when the app allows guests, passkeys stay in page
 * only on a first-party host, and the footer links point at the apex
 * because a root binding would swallow `/doc/...` here. On the apex after
 * a hop only the header is branded and the named provider may auto-start.
 *
 * TWO SCREENS IN ONE COMPONENT (2026-10-02, app-sign-in-and-onboarding.md
 * section 4, decisions 4 and 5):
 *
 *   - THE PLATFORM'S OWN SCREEN, when nothing names an app: the Robutler
 *     mark, every configured provider as a button in the platform's order,
 *     the passkey button, all visible as before.
 *   - AN APP'S SCREEN, whenever the screen names an app (a verified host
 *     brand, the apex after a hop, the `/?app=` funnel, the workspace
 *     dialog): a lean panel with the app's own mark, "Sign in to <App>", one
 *     line, the app's FIRST TWO providers as buttons, and every other way
 *     the platform has (the rest of the providers and the passkey) one tap
 *     away behind "Other ways to sign in", so a person who made their
 *     account another way can still get in. No card around the buttons, no
 *     second paragraph. Under the buttons: "<App> runs on Robutler." and the
 *     terms sentence. On the standalone page an app with a picture gets the
 *     FULL-BLEED screen (the owner, 2026-10-02, "that's boring layout! do
 *     full bleed full screen!", about the first cut: a 780 px block with a
 *     square picture beside the panel, centred on an empty page): the app's
 *     colour fills the right half of the screen from `md` up, and a band
 *     across the top at phone width, with the picture running off its edges,
 *     and the panel holds the rest. In the workspace dialog
 *     (`surface="dialog"`) the picture is small, above the heading; with no
 *     picture the panel centres alone.
 *
 * WHAT THE BAND DRAWS, AND WHOSE WORDS (the owner, 2026-10-04 night, on the
 * wireframes for the Data Phoenix send). The full-bleed screen is drawn for
 * every app that has a sign-in screen, and its band holds ONE of three
 * things: the project's picture; two people joined by a line when a scanned
 * code is waiting ("for code show two avatars (those who sent you and you)
 * and a connection line"), the inviter as the initial of the name the screen
 * already draws and the arriving person as an open circle; or NOTHING BUT
 * THE APP'S COLOUR where no picture is set. The product screenshot that
 * stood in the last two is gone. The app's name stood on the colour for a
 * few hours ("just Revent wordmark for generic login where hero is not
 * set") and the owner, seeing it over "Sign in to Revent", asked for the
 * event's name there, "or nothing, just grad if no name available"
 * (2026-10-05). No name is available to this screen: a sent link carries
 * ids and never a title (lib/auth/arrival-fragment.ts), and a title must
 * not come from the link, or anyone could set any words in large type on
 * the platform's own sign-in page. So the band draws the EVENT'S NAME only
 * where the platform can look it up itself (the owner: "ok, lets build
 * it"): for a sent event of the built-in week the screen asks
 * /api/auth/sign-in-event with the link's event id, a key, and draws the
 * title the platform's own published catalog holds
 * (lib/apps/revent-event-names.ts). Until the answer comes, and whenever
 * there is none, the band is the colour alone. A project may word its own heading; it is drawn exactly
 * where the project's line is, and because it no longer says which app this
 * is, the app's name is then drawn under its mark, as "<App> App" so that
 * somebody who has never heard the name knows what it is (a project may word
 * that label too: SF Tech Week's is "Free Revent App"). A project may also name
 * who sent the person: the campaign's source tag picks a name out of the
 * project's fixed table and "<Partner> x Robutler" signs the foot, above
 * "<App> runs on Robutler." and the terms (lib/auth/login-app.ts
 * `signInPartnerName`; the tag is a key and never text). It stood under the
 * heading for an hour and made six rows of text in one block.
 *
 * WHICH PROVIDERS. One list for the whole platform
 * (lib/auth/sign-in-providers.ts): an app may only narrow and reorder it
 * (`signInProvidersFor`), and a provider is drawn only where THIS
 * environment has its credentials (`signInProviders` in the runtime config,
 * read through `availableSignInProviders`, which treats a config that has
 * not arrived as the three providers every environment has). Until LinkedIn
 * is configured, Revent's first button is therefore Google. For Monday an
 * app's list and screen are constants on its `FLAGSHIP_APPS` entry; the
 * owner-written setting comes the week after and will be read the same way.
 *
 * A CONFERENCE SUPPLIES ITS OWN SCREEN (step 1, 2026-10-13; the owner: "go for
 * the conference-supplied sign-in screen"). A conference the operator's
 * publisher wrote a sign-in screen into (scripts/revent-catalog.ts
 * `--sign-in`) draws ITS words and ITS two pictures: the heading and the line
 * as text in the reader's language (English where the data has none), the
 * pictures from our own route, never a remote image
 * (lib/auth/published-sign-in.ts builds every path). The constants are the
 * fallback, in this order: the published screen, else the project's
 * constants, else the app's own line and colour; the label under the mark
 * and the partner table stay the constants'. `published` is what the dynamic
 * `/login/app` page looked up for the first paint (the conference the link
 * names and the app's default one); anything else, and every screen on the
 * static `/login` and in the workspace dialog, is asked for once from
 * /api/auth/sign-in-screen with the conference's folder id. Until it answers,
 * the screen is the constants', exactly as before. A conference that is in
 * no constant table and has a published screen becomes a project of its own
 * (its link is its own link, not a sent one: lib/auth/login-app.ts). Never on
 * an app's own host: the route is the apex's, and the host keeps the
 * constants.
 */
export function LoginDialogBody({
  params,
  mode,
  brand = null,
  surface = 'dialog',
  published = null,
}: {
  params: URLSearchParams;
  mode: 'login' | 'signup';
  brand?: LoginBrand | null;
  /**
   * `page` is the standalone sign-in page (`/login`, `/login/host`), which
   * owns the whole screen and so can draw an app's full-bleed screen;
   * `dialog` (the default, what the workspace dialog stack passes by passing
   * nothing) draws the compact treatment.
   */
  surface?: 'page' | 'dialog';
  /**
   * Published conference screens the server page already looked up, by
   * conference folder id; null for a conference it looked up and found none
   * for. Validated again here (`screensFromWire`).
   */
  published?: Readonly<Record<string, SignInScreenWire | null>> | null;
}) {
  const { t, locale } = useTranslation();
  const { trackAuthStart } = useAnalytics();
  const ref = params.get('ref');
  const error = params.get('error');
  const onHost = !!brand?.onHost;
  // Accept both query names — `returnTo` is the canonical post-auth
  // forward param (used by /workspace, the OAuth callbacks, and the
  // landing-page hero composer carryover), while older surfaces still
  // emit `redirect`. Either is fine; we just hand whichever the user
  // arrived with to the chosen provider start route.
  //
  // Sanitised on the way in (S-059, 2026-09-12). This value reaches
  // `window.location.href` on the passkey path below, and it is whatever was
  // in the address bar, so an absolute URL here walked somebody through a real
  // sign-in and then handed them to another site from our own origin. Same
  // rule as `safeReturnTo` on the server: a single-leading-slash path or
  // nothing. Kept inline rather than imported because `lib/auth/domain-session`
  // is server-only.
  const rawReturnTo = params.get('returnTo') || params.get('redirect');
  // The shared rule since S-160 (2026-09-18): the inline copy that stood here
  // let a TAB through, which the URL parser drops, so `/\t/evil.example`
  // resolved off-site.
  const returnTo = isSafeReturnPath(rawReturnTo) ? rawReturnTo : null;
  const provider = params.get('provider');
  const link = params.get('link');

  const authParams = new URLSearchParams();
  if (ref) authParams.set('ref', ref);
  if (returnTo) authParams.set('returnTo', returnTo);
  // The mode survives the OAuth round-trip so the callback can
  // decide whether to run clone-on-signup. We pack it into the
  // outbound URL; each provider's start route forwards it to the
  // OAuth state blob.
  authParams.set('signupMode', mode);
  // `forceAccountChoice=1` on the login URL is forwarded to whichever provider
  // the person picks, so the provider shows an account picker instead of
  // silently reusing whichever account this browser is already signed into:
  // Google shows its picker, Discord pins its authorize screen (whose "Not
  // you?" link is its account switch), X has no such parameter (see
  // `getXAuthUrl`). For somebody who wants to sign in or sign up with a
  // different identity. A bare flag, never a value: anything but '1' is
  // dropped, and without it every provider URL below is exactly what it was.
  if (params.get('forceAccountChoice') === '1') authParams.set('forceAccountChoice', '1');
  const qs = authParams.toString();

  const discordParams = new URLSearchParams(authParams);
  if (link) discordParams.set('link', link);
  const discordQs = discordParams.toString();

  // ON A HOST every provider button starts the apex hand-off with the
  // provider named, and `returnTo` is the path on the HOST the person came
  // from. The hop cannot carry `ref`, `signupMode` or `forceAccountChoice`:
  // the apex login receives `provider` and `host` and builds its own links.
  const hostStartUrl = (which: SignInProvider | 'guest'): string => {
    const q = new URLSearchParams();
    if (which === 'guest') q.set('guest', '1');
    else q.set('provider', which);
    if (returnTo) q.set('returnTo', returnTo);
    return `/api/auth/domain/start?${q.toString()}`;
  };
  // The one place a provider's start URL is built: the shared module's
  // apex path with this screen's query (Discord also carries `link`), or
  // the host hop. The buttons, the auto-start and the in-app sheet all read it.
  const startUrl = (which: SignInProvider): string => {
    if (onHost) return hostStartUrl(which);
    const q = which === 'discord' ? discordQs : qs;
    return `${SIGN_IN_PROVIDER_INFO[which].startPath}${q ? `?${q}` : ''}`;
  };
  const guestUrl = onHost && brand?.allowGuest ? hostStartUrl('guest') : null;

  // Footer links live on the apex. On a host a root binding is a prefix of
  // everything, so a relative `/doc/terms-of-service` would open the app.
  const apexBase = onHost ? getRuntimeConfig().baseUrl.replace(/\/$/, '') : '';

  // THE ARRIVAL FRAGMENT (2026-09-20, the Revent first-touch e2e run). A scan
  // of Revent's code lands here as
  // `/login?signup=1&returnTo=%2F%3Fapp%3Drevent#c=<code>&n=<name>&e=<event>`
  // with the fragment intact, because the server redirect that sent it here
  // carries no fragment of its own and the browser keeps the original. The
  // provider links above are built from the query string alone, so the click
  // on one of them started a navigation without it and the person came out of
  // the OAuth round trip holding nothing. Park it in sessionStorage, client
  // side only (it is a live one-use code and must never reach a server or a
  // log), for `ArrivalFragmentRestore` to put back on the destination URL. Only
  // a fragment in the allowlisted grammar, and only next to a `returnTo` that
  // passed `isSafeReturnPath`: the stash cannot change where the person goes,
  // it can only decorate that page. It is never cleared here: a provider
  // error bounces back to this screen without the fragment, and the retry
  // should still carry it (the TTL bounds how long). Declared ahead of the
  // Discord auto-redirect so it has run by the time that fires.
  useEffect(() => {
    /* THE QUERY FORM FIRST (2026-10-11, lib/auth/arrival-query.ts): a link
       that names a conference or an event in its query
       (`/?app=revent&conference=la-2026`) reaches this screen with the two
       parameters beside the return address (the apex) or inside it (an
       app's own host). They become the arrival fragment here, before
       anything below reads the hash, so the stash, the conference's own
       screen and the event's name all work off the one grammar. A fragment
       already on the address wins and nothing is written. */
    try {
      adoptArrivalQuery(window, { returnTo });
    } catch {
      /* no history to write to: the link opens the default screen */
    }
    try {
      stashArrivalFragment({
        hash: window.location.hash,
        returnTo,
        storage: window.sessionStorage,
      });
    } catch {
      /* no storage (opaque origin, blocked): the fragment is simply not carried */
    }
  }, [returnTo]);

  // WHICH APP THIS SIGN-IN OPENS (2026-09-25, the Revent mobile first touch).
  // `/?app=revent` lands here with that path as `returnTo`, and a scanned
  // code may be waiting on the hash or in the stash above; either one names
  // Revent in the heading instead of "Sign up for Robutler". The `returnTo`
  // half is pure and resolved on the first render, so the heading never
  // flashes generic; the hash and storage halves need a window and land one
  // effect later. Only an app in FLAGSHIP_APPS can be named, and nothing the
  // fragment carries is ever drawn here (lib/auth/login-app.ts).
  //
  // THE PUBLISHED SCREENS (2026-10-13, the header). Those the page handed
  // over, and those asked for below, by conference folder id; a conference
  // with one is known to the resolution, so its link opens its own screen.
  // The fragment is kept as it was read, so the app is resolved again when an
  // answer comes in, without reading the hash or the stash twice.
  const [screens, setScreens] = useState<Readonly<Record<string, PublishedSignInScreen | null>>>(() =>
    screensFromWire(published),
  );
  const publishedIds = useMemo(() => Object.keys(screens).filter((id) => !!screens[id]), [screens]);
  const returnToApp = useMemo(() => resolveLoginApp({ returnTo, published: publishedIds }), [returnTo, publishedIds]);
  const [arrival, setArrival] = useState<{ fragment: string | null } | null>(null);
  useEffect(() => {
    let storage: Storage | null = null;
    try {
      storage = window.sessionStorage;
    } catch {
      storage = null;
    }
    setArrival({ fragment: parseArrivalFragment(window.location.hash) ?? peekArrivalFragment(storage) });
  }, [returnTo]);
  const arrivalApp = useMemo(
    () => (arrival ? resolveLoginApp({ returnTo, fragment: arrival.fragment, published: publishedIds }) : null),
    [arrival, returnTo, publishedIds],
  );
  /* AN APP'S OWN SCREEN IS OPT-IN (2026-10-13, the owner: "let's just make
     this login screen look like /login if the app does not provide (opt in)
     a custom login screen"). Only an app whose flagship entry carries a
     `signInScreen` (today Revent) draws its own heading, line and art; any
     other app named by the return address gets exactly Robutler's /login,
     and the return address still opens the app in the person's workspace
     after sign-in. It replaced "Sign in to Docs", which the landing's "Open
     app" showed for every app. */
  const resolvedApp = arrivalApp ?? returnToApp;
  const loginApp = resolvedApp && FLAGSHIP_BY_APP_ID.get(resolvedApp.appId)?.signInScreen ? resolvedApp : null;

  // THE SCREEN'S APP. A verified host brand outranks the funnel's own guess;
  // either way the flagship entry (if the app is one) carries the screen's
  // constants: its provider order, its line, its mark and its images. A
  // brand names a flagship only for a first-party host (`LoginBrand.appId`).
  const flagship: FlagshipApp | null = brand
    ? brand.appId
      ? (FLAGSHIP_BY_APP_ID.get(brand.appId) ?? null)
      : null
    : loginApp
      ? (FLAGSHIP_BY_APP_ID.get(loginApp.appId) ?? null)
      : null;
  const screen = flagship?.signInScreen ?? null;
  /** The screen names an app: the lean panel, not the platform's own screen. */
  const named = !!brand || !!loginApp;
  const appName = brand?.name ?? loginApp?.title ?? '';
  // THE PROJECT'S LINE AND PICTURE (the owner, 2026-10-04: "make the login
  // image defined by project"; lib/workspaces/flagship-apps.ts). The funnel
  // and an arrival say which project the sign-in is for (`loginApp.project`,
  // only ever a key of the app's own table); a host page with nothing
  // arriving opens the app's default project, as the bare funnel does. No
  // project the screen knows: the app's own line and picture, so one
  // project's art is never drawn for another.
  const projectKey =
    loginApp && flagship && loginApp.appId === flagship.appId
      ? loginApp.project
      : (screen?.defaultProject ?? null);
  // THE CONFERENCE TO ASK ABOUT (2026-10-13, the header): the one the link
  // names, else the project the screen is about to draw (the default for the
  // bare link). Not for a scanned code (its band is the two people, and its
  // line the invitation), not on an app's own host, and once per conference.
  // Not before the arrival has been read either: until then the screen can
  // only guess the default, and a link that names another conference would
  // cost a question about the wrong one.
  const linkConference = arrival?.fragment ? conferenceFromFragment(arrival.fragment) : null;
  const askConference =
    arrival && !onHost && flagship?.appId === ARRIVAL_FRAGMENT_APP_ID && !loginApp?.codePending
      ? (linkConference ?? projectKey)
      : null;
  const askedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const id = askConference;
    if (!id || !isConferenceFolderId(id) || askedRef.current.has(id) || hasOwn(screens, id)) return;
    askedRef.current.add(id);
    // A failed, refused or malformed answer is "no published screen": the
    // constants stay, and the conference is not asked about again.
    const settle = (found: PublishedSignInScreen | null) =>
      setScreens((prev) => (hasOwn(prev, id) ? prev : { ...prev, [id]: found }));
    try {
      fetch(`${SIGN_IN_SCREEN_ROUTE}?conference=${encodeURIComponent(id)}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((body: { screen?: unknown } | null) =>
          settle(body && typeof body === 'object' ? screenFromWire(id, body.screen) : null),
        )
        .catch(() => settle(null));
    } catch {
      settle(null);
    }
  }, [askConference, screens]);
  // THE PROJECT'S WORDS AND PICTURES: the published screen, else the
  // project's constants (the header, "a conference supplies its own screen").
  const constProject =
    projectKey && screen?.projects && hasOwn(screen.projects, projectKey) ? screen.projects[projectKey] : null;
  const dataProject =
    projectKey && flagship?.appId === ARRIVAL_FRAGMENT_APP_ID && hasOwn(screens, projectKey) ? screens[projectKey] : null;
  const projectScreen: ProjectScreenView | null = dataProject
    ? {
        source: 'published',
        heading: pickSignInText(dataProject.heading, locale),
        line: pickSignInText(dataProject.line, locale),
        image: dataProject.image,
        nameKey: constProject?.nameKey ?? null,
        partners: constProject?.partners ?? null,
      }
    : constProject
      ? {
          source: 'constants',
          heading: constProject.headingKey ? t(constProject.headingKey, { app: appName }) : null,
          line: t(constProject.lineKey),
          image: constProject.image,
          nameKey: constProject.nameKey ?? null,
          partners: constProject.partners ?? null,
        }
      : null;
  // A SCANNED CODE IS AN INVITATION (the owner, 2026-10-03, on the paragraph
  // that stood under the line: "'Someone at tech week showed...' too boring
  // and confusing, drop; instead just say that person Firstname Lastname
  // invited you to connect on Revent"). When a code is waiting for this
  // sign-in, and the screen's app is the one the code belongs to, the ONE line
  // says who invited the person; the name is drawn only when it reads as a
  // person's name (lib/auth/login-app.ts `inviterNameFromFragment`), and the
  // line says the same without a name otherwise.
  const invited = !!loginApp?.codePending && (!brand || brand.appId === loginApp.appId);
  /** A link somebody sent names an event: its own line under the app's heading. */
  const sentLinkIntroKey =
    !invited && !brand && loginApp?.sentLink
      ? ((loginApp.sentConference ? APP_CONFERENCE_LINK_INTRO_KEY[loginApp.appId] : null) ?? APP_LINK_INTRO_KEY[loginApp.appId] ?? null)
      : null;
  // THE PROJECT'S OWN WORDS are drawn only when the screen is ABOUT the
  // project: never over an invitation or a sent link, whose lines say
  // something else and would sit under the wrong promise.
  const projectWords = projectScreen && !invited && !sentLinkIntroKey ? projectScreen : null;
  const ownHeading = projectWords?.heading ?? null;
  /** Who sent the person, from the campaign's source tag: a name out of the project's fixed table, or nothing. */
  const partner = projectWords ? signInPartnerName(projectWords.partners, params.get('utm_source')) : null;
  // No picture for an invitation: on the page its band draws the two people,
  // and in the dialog the app's mark stands where a picture would.
  const image = named && !invited ? (projectScreen ? projectScreen.image : (screen?.image ?? null)) : null;
  /** What the band of the full-bleed screen holds (`plain` is the app's colour alone); null for an app with no sign-in screen. */
  const art: 'pair' | 'picture' | 'plain' | null =
    named && screen && flagship ? (invited ? 'pair' : image ? 'picture' : 'plain') : null;
  /** The standalone page and an app with a sign-in screen: the full-bleed screen. */
  const fullBleed = art !== null && surface === 'page';
  // THE NAME OF A SENT EVENT (2026-10-05; the header, "what the band draws").
  // Asked for once per event, only for a sent link that names an event of the
  // built-in week, and only where there is a band with nothing in it to draw
  // the name in (the full-bleed page, no picture, no invitation). The link's
  // id is sent, nothing else of the link; the answer is drawn as text, and
  // only when it is a string of a name's length. A slow, failed or empty
  // answer changes nothing: the band keeps the app's colour.
  const sentEvent = sentLinkIntroKey && fullBleed && art === 'plain' ? (loginApp?.sentEvent ?? null) : null;
  const [sentEventName, setSentEventName] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    if (!sentEvent) return;
    let live = true;
    try {
      fetch(`/api/auth/sign-in-event?e=${encodeURIComponent(sentEvent)}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((body: { name?: unknown } | null) => {
          const name = typeof body?.name === 'string' ? body.name.trim() : '';
          if (live && name && Array.from(name).length <= SENT_EVENT_NAME_MAX) setSentEventName({ id: sentEvent, name });
        })
        .catch(() => {
          /* no name: the band stays the app's colour */
        });
    } catch {
      /* no fetch here: the same */
    }
    return () => {
      live = false;
    };
  }, [sentEvent]);
  const eventName = sentEvent && sentEventName?.id === sentEvent ? sentEventName.name : null;

  // WHICH PROVIDERS THIS ENVIRONMENT HAS. On a dynamic page, and in the
  // workspace dialog, the inline runtime config is live and carries the list.
  // On the STATIC apex /login it does not (2026-10-02, seen on dev the day it
  // shipped): that page is prerendered with no credentials and the deferred
  // `/api/runtime-config.js` repair never executes there, so Revent's screen
  // drew Google first on an environment that has LinkedIn. Without a live
  // list the screen asks /api/auth/sign-in-providers once and draws
  // placeholder buttons of the same size until it answers, so no button
  // changes under a finger; a failed or slow answer falls back to the
  // providers every environment has (`availableSignInProviders`). `null`
  // means "not known yet".
  const [configured, setConfigured] = useState<SignInProvider[] | null>(() => {
    const live = getRuntimeConfig().signInProviders;
    return Array.isArray(live) && live.length > 0 ? availableSignInProviders(live) : null;
  });
  useEffect(() => {
    if (configured) return;
    let settled = false;
    const settle = (list: unknown) => {
      if (settled) return;
      settled = true;
      setConfigured(availableSignInProviders(Array.isArray(list) ? list : null));
    };
    const timer = window.setTimeout(() => settle(null), SIGN_IN_PROVIDERS_WAIT_MS);
    fetch('/api/auth/sign-in-providers', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { providers?: unknown } | null) => settle(body?.providers ?? null))
      .catch(() => settle(null))
      .finally(() => window.clearTimeout(timer));
    return () => {
      settled = true;
      window.clearTimeout(timer);
    };
  }, [configured]);
  const providersKnown = configured !== null;
  const knownProviders = useMemo(() => configured ?? [], [configured]);
  // Every configured provider in the platform's order, and the app's own
  // order where the screen names an app (narrow and reorder only). On an
  // app's screen the first two of the app's list are the buttons; the rest
  // of its list AND every provider the app left out follow behind "Other
  // ways to sign in", in the platform's order, so a person who made their
  // account with a provider the app hid can still get in (section 4.1).
  const everyProvider = useMemo(() => signInProvidersFor(null, knownProviders), [knownProviders]);
  const appProviders = useMemo(
    () => signInProvidersFor(named ? (flagship?.signInProviders ?? null) : null, knownProviders),
    [named, flagship, knownProviders],
  );
  const primaryProviders = named ? appProviders.slice(0, APP_SCREEN_PRIMARY_COUNT) : everyProvider;
  const moreProviders = named
    ? [...appProviders.slice(APP_SCREEN_PRIMARY_COUNT), ...everyProvider.filter((slug) => !appProviders.includes(slug))]
    : [];
  /** Every provider this screen offers, buttons first, for the in-app sheet. */
  const providers = named ? [...primaryProviders, ...moreProviders] : everyProvider;

  // THE IN-APP BROWSER (2026-09-25, design section 2.4). Inside LinkedIn's,
  // Instagram's or Facebook's embedded browser Google refuses sign-in
  // outright (`disallowed_useragent`), so the way out (open in Chrome or
  // Safari, or copy the link, fragment and all) is shown BEFORE the Google
  // button, as a dismissible sheet. Detected after mount from the user agent
  // (lib/auth/in-app-browser.ts): false on the server and on the first
  // paint. A home-screen web app has a web view's user agent but is the
  // person's own choice, so it is not one. Not shown where a guest
  // "Continue" is the primary action: that path works inside a web view and
  // the sheet would only stand in front of it.
  const [inApp, setInApp] = useState<InAppBrowserDetection | null>(null);
  const [inAppDismissed, setInAppDismissed] = useState(false);
  const [pageHref, setPageHref] = useState('');
  useEffect(() => {
    let standalone = false;
    try {
      standalone =
        (typeof window.matchMedia === 'function' &&
          window.matchMedia('(display-mode: standalone)').matches) ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true;
    } catch {
      standalone = false;
    }
    const detection = detectInAppBrowser(navigator.userAgent, { standalone });
    if (detection.inApp) {
      setInApp(detection);
      setPageHref(window.location.href);
    }
  }, []);

  // AUTO-START. Discord has always auto-started from `?provider=discord`
  // (the Discord install flow lands here). After a hop from a host the apex
  // login also auto-starts the provider the person already pressed THERE
  // (plan D2), so the hop is one click and not two. Never on the host page
  // itself, where the person is choosing, and never after an error. Never
  // Google from inside an in-app browser either: that start would only land
  // on Google's refusal page, and the sheet above has the way out. A LOOKUP
  // against the one list (2026-10-02): the expression that stood here sent
  // any provider it did not know to the X start, and a provider this
  // environment has no credentials for starts nothing rather than a 503.
  const autoProvider: SignInProvider | null =
    !error &&
    !onHost &&
    isSignInProvider(provider) &&
    knownProviders.includes(provider) &&
    (provider === 'discord' || !!brand)
      ? provider
      : null;
  const autoStart = autoProvider ? (autoProvider === 'google' && inApp ? null : startUrl(autoProvider)) : null;
  useEffect(() => {
    if (!autoStart) return;
    const tid = window.setTimeout(() => {
      window.location.href = autoStart;
    }, 50);
    return () => window.clearTimeout(tid);
  }, [autoStart]);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);

  // `mode` from the caller is authoritative; we keep the
  // explicit-signup / hasLoggedInBefore probe so the legacy
  // standalone `/login?signup=1` URL still resolves to the right
  // copy when rendered as the SSR fallback page.
  const explicitSignup = params.get('signup') === '1';
  const [hasLoggedInBefore, setHasLoggedInBefore] = useState(false);
  useEffect(() => {
    try {
      const ls = window.localStorage;
      if (
        ls.getItem('robutler_has_logged_in') === '1' ||
        ls.getItem('robutler_onboarding_done') === '1'
      ) {
        setHasLoggedInBefore(true);
      }
      // The localStorage flag is written by the apex when a passkey is
      // registered there, so on a host it is never set and the button never
      // showed (functional review, 2026-09-24). A first-party host is listed
      // as a related origin, so there the button shows whenever the browser
      // has WebAuthn at all; the assertion itself tells a person without a
      // passkey, as it does on the apex.
      const passkeyKnown = ls.getItem('robutler_passkey_registered') === '1';
      if (
        (passkeyKnown || (onHost && !!brand?.passkeys)) &&
        typeof window !== 'undefined' &&
        window.PublicKeyCredential
      ) {
        setPasskeyAvailable(true);
      }
    } catch {
      /* ignore */
    }
  }, []);
  // A consumption-only session is decided on the FIRST document of the tab
  // (plan G4): `document.referrer` starts with `android-app://` there and
  // nowhere after. When a store build opens a host for a signed-out
  // visitor, that first document is THIS page rather than the app, so the
  // referrer is remembered here as well as in standalone-widget.tsx
  // (functional review, 2026-09-24).
  useEffect(() => {
    if (!onHost) return;
    let storage: Storage | null = null;
    try {
      storage = window.sessionStorage;
    } catch {
      storage = null;
    }
    rememberTwaReferrer(document.referrer, storage);
  }, [onHost]);
  const isLoginMode =
    mode === 'login' && !explicitSignup ? true : mode === 'signup' ? false : hasLoggedInBefore;
  // Passkeys in page: always on the apex; on a host only where the apex
  // lists the host as a related origin (first-party hosts, plan D3). The
  // route refuses everywhere else, so this only hides a button that would
  // fail.
  const showPasskey = passkeyAvailable && (brand ? brand.passkeys : true);

  // "OTHER WAYS TO SIGN IN" (decision 5): on an app's screen the providers
  // past the first two and the passkey open in place behind one control, a
  // real button with `aria-expanded`. Nothing to put there, no control.
  const [moreOpen, setMoreOpen] = useState(false);
  const moreId = useId();
  const hasMore = named && providersKnown && (moreProviders.length > 0 || showPasskey);
  // NOTHING MOVES AROUND THE CONTROL (the full-bleed screen, 2026-10-03). At
  // phone width the panel sits at the BOTTOM of the screen (the owner: "anchor
  // the signin form to the bottom?") and the picture's band takes what is left
  // above it. Left to the layout, opening the list would shorten the band and
  // lift the panel under the finger, and closing it would let the page shrink
  // and slide the buttons into the spot just pressed. Either way the next
  // press could land on a provider, which is the defect the visual check of
  // 2026-10-02 found on the centred layout (a second press started a real
  // sign-in). So the first press FREEZES the layout as it stands: the band
  // keeps its height in pixels, and the panel's column never gets shorter than
  // the tallest it has been. Opening then only grows the page downwards, and
  // closing leaves everything else where it was. A change of WIDTH (a
  // rotation, a desktop resize) lets go; a change of height alone does not,
  // because a phone's address bar hiding on scroll is one.
  const artRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const [frozen, setFrozen] = useState<{ band: number; column: number; width: number } | null>(null);
  const toggleMore = () => {
    const band = artRef.current?.getBoundingClientRect().height ?? 0;
    const column = columnRef.current?.getBoundingClientRect().height ?? 0;
    if (band > 0 && column > 0) {
      setFrozen((prev) => ({
        band: prev?.band ?? Math.round(band),
        column: Math.max(prev?.column ?? 0, Math.ceil(column)),
        width: prev?.width ?? window.innerWidth,
      }));
    }
    setMoreOpen((open) => !open);
  };
  const frozenWidth = frozen?.width ?? null;
  useEffect(() => {
    if (frozenWidth === null) return;
    const release = () => {
      if (window.innerWidth !== frozenWidth) setFrozen(null);
    };
    window.addEventListener('resize', release);
    return () => window.removeEventListener('resize', release);
  }, [frozenWidth]);

  const providerButton = (slug: SignInProvider, primary: boolean) => (
    <a
      key={slug}
      href={startUrl(slug)}
      onClick={() => trackAuthStart(slug)}
      data-testid={`login-provider-${slug}`}
      className={cn(
        primary ? 'btn-primary' : 'btn-outline',
        'w-full justify-center flex items-center gap-2',
        // 44 px on a phone on an app's screen (the visual check, 2026-10-02:
        // the house 36 px under a thumb); the platform's own screen keeps
        // the house height.
        named && 'max-md:h-11',
      )}
    >
      <ProviderIcon slug={SIGN_IN_PROVIDER_INFO[slug].iconSlug} size={16} />
      {t(SIGN_IN_PROVIDER_INFO[slug].labelKey)}
    </a>
  );
  // Same box as a button, so the buttons that replace them move nothing.
  const providerPlaceholders = (count: number) =>
    Array.from({ length: count }, (_, i) => (
      <div
        key={`placeholder-${i}`}
        aria-hidden
        data-testid="login-provider-placeholder"
        className={cn('btn-outline w-full animate-pulse opacity-60', named && 'max-md:h-11')}
      />
    ));

  const passkeyButton = (
    <div>
      <button
        type="button"
        data-testid="login-passkey"
        onClick={async () => {
          trackAuthStart('passkey');
          setPasskeyError(null);
          try {
            const { startAuthentication } = await import('@simplewebauthn/browser');
            const optionsRes = await fetch('/api/auth/webauthn/authenticate');
            if (!optionsRes.ok) {
              setPasskeyError(t('auth.passkeyNotAvailable'));
              return;
            }
            const options = await optionsRes.json();
            const authResp = await startAuthentication({ optionsJSON: options });
            const verifyRes = await fetch('/api/auth/webauthn/authenticate', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(authResp),
            });
            const result = await verifyRes.json();
            if (result.verified) {
              // On a host the app lives at `/`; the apex lands in the
              // workspace as before.
              window.location.href = returnTo || (onHost ? '/' : '/workspace?onboarding=1');
            } else {
              setPasskeyError(t('auth.passkeyVerificationFailed'));
            }
          } catch (err: any) {
            if (err.name === 'NotAllowedError') {
              // user cancelled - no error
            } else {
              setPasskeyError(t('auth.noPasskeyFound'));
            }
          }
        }}
        className="btn-outline w-full justify-center flex items-center gap-2"
      >
        <Fingerprint className="h-4 w-4" />
        {t('auth.signInWithPasskey')}
      </button>
      {passkeyError && (
        <div className="mt-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {passkeyError}
        </div>
      )}
    </div>
  );

  const guestBlock = guestUrl ? (
    <div className="space-y-2">
      {/* The guest hop (plan section 14): the primary action on a host
          whose app allows guests. Minted on the apex, handed back
          through the same code as a sign-in. */}
      <a
        href={guestUrl}
        onClick={() => trackAuthStart('guest')}
        data-testid="login-continue-guest"
        className="btn-primary w-full justify-center flex items-center gap-2"
      >
        <ArrowRight className="h-4 w-4" />
        {t('auth.continueAsGuest')}
      </a>
      <p className="text-center text-xs text-muted-foreground">{t('auth.guestHint')}</p>
      <div className="flex items-center gap-3 pt-1 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        {t('auth.orSignIn')}
        <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  ) : null;

  // THE MARK above the heading. The app's own on an app's screen: the
  // verified row's icon on a host (or after a hop), the flagship's constant
  // mark on the funnel, and in the dialog the app's small square image where
  // there is one. The Robutler mark only on the platform's own screen and
  // for a named screen with nothing better to draw. On the full-bleed screen
  // the mark is a size up and wears a ring of the page's own background: at
  // phone width it sits across the lower edge of the coloured band (the panel
  // column below is pulled up over it), and the ring cuts it out of the band.
  // THE APP'S NAME UNDER ITS MARK, where the heading is the project's own
  // and so does not say which app this is (the owner, 2026-10-04 night:
  // "'Revent' app is not there? where are the users signing in into?
  // unclear"). The mark then sits closer to it, as an app's label sits under
  // its icon.
  const nameUnderMark = named && !!ownHeading;
  // THE RHYTHM OF THE FULL-BLEED PANEL (the owner on the first build,
  // 2026-10-05: "feels a bit too crammed... move the icon higher? we need
  // more balanced spacings", and of three versions drawn on the real page,
  // "do roomier"). The mark and its name are one thing (8 px apart, the name
  // small and light), then the promise and its line (22 px under the name,
  // 12 px between them), then the buttons (32 px on). It had been 6, 10, 8
  // and 24 px, with the name as heavy as the heading. The room comes from the
  // mark, which sits its whole height above the band's lower edge at phone
  // width (see the column and the fade below). With no name the mark stands
  // 24 px above the heading. The lean screens keep the house gaps.
  const markGap = nameUnderMark ? 'mb-2' : fullBleed ? 'mb-6' : 'mb-4';
  const markClass = fullBleed
    ? cn('mx-auto h-14 w-14 rounded-2xl shadow-md ring-4 ring-background', markGap)
    : cn('mx-auto h-12 w-12 rounded-xl', markGap);
  // A MARK DRAWN FOR THE SCREEN keeps its own tile (the flagship's
  // `mark.tile`, 2026-10-10: LA Tech Week's mock, a ground that runs straight
  // down and a 24 px glyph): the ground is used as written and the glyph is
  // the size it names. Every other mark is the house tile, the colour on the
  // cover gradient under a 28 px glyph. A host page draws the row's mark and
  // never reads this.
  const ownTile = !brand ? (screen?.mark.tile ?? null) : null;
  const markIconClass = fullBleed ? (ownTile?.glyphPx === 24 ? 'h-6 w-6' : 'h-7 w-7') : 'h-6 w-6';
  const mark = named ? (
    image && surface === 'dialog' ? (
      <img
        src={image.phone}
        alt=""
        draggable={false}
        data-testid="login-screen-image-compact"
        className={cn('mx-auto h-24 w-24 rounded-2xl object-cover ring-1 ring-border', markGap)}
      />
    ) : brand ? (
      <WidgetMark
        iconRef={brand.icon}
        seed={brand.contentId}
        name={brand.name}
        // The host's own colour (the app's tone, or the one its owner set),
        // so the mark here is the mark the workspace draws for the same app.
        color={brand.themeColor}
        className={markClass}
        iconClassName={markIconClass}
      />
    ) : screen && flagship ? (
      <WidgetMark
        iconRef={{ kind: 'lucide', name: screen.mark.icon }}
        seed={flagship.appId}
        name={flagship.title}
        className={markClass}
        iconClassName={markIconClass}
        style={{ background: ownTile?.ground ?? coverGradient(flagship.appId, screen.mark.color).css }}
      />
    ) : (
      <RobutlerLogo width={48} height={48} className="mx-auto mb-4 text-foreground" />
    )
  ) : (
    <RobutlerLogo width={56} height={56} className="mx-auto mb-4 text-foreground" />
  );

  const heading = named
    ? (ownHeading ?? t(screen?.headingKey ?? 'auth.signInToApp', { app: appName }))
    : isLoginMode
      ? t('auth.loginTitle')
      : t('auth.signupTitle');

  // ONE LINE under the heading: the invitation when a code is waiting (decided
  // above, where the picture is), the sent-link line when a Revent link named
  // an event, the project's own, the app's own, a generic app line when the
  // app has none, and the platform's line on its own screen.
  const line = !named
    ? t('auth.agentsPageDescription')
    : invited && loginApp
      ? loginApp.invitedBy
        ? t('auth.arrivalInvitedBy', { name: loginApp.invitedBy, app: appName })
        : t('auth.arrivalInvited', { app: appName })
      : sentLinkIntroKey
        ? t(sentLinkIntroKey)
        : projectScreen
          ? projectScreen.line
          : screen
            ? t(screen.lineKey, { app: appName })
            : t('auth.appLineGeneric', { app: appName });

  const panel = (
    <div className="w-full max-w-[350px] mx-auto">
      <div className={cn('text-center', fullBleed ? 'mb-8' : 'mb-6')}>
        {mark}
        {/* EACH OF THE THREE LINES TAKES ITS DIRECTION FROM ITS OWN WORDS
            (`dir="auto"`, 2026-10-10). The page is laid out left to right in
            every language, Arabic included, so an Arabic sentence drawn here
            put its closing full stop at the RIGHT end, before its first word
            (twice in the two-sentence line), and the app's name, Arabic words
            on both sides of "Revent", read with those words in the opposite
            order. The text stays centred, and a line that begins in a
            left-to-right script is drawn as before. */}
        {nameUnderMark ? (
          <p
            className="font-display text-sm font-medium tracking-tight text-foreground mb-5.5"
            data-testid="login-app-name"
            dir="auto"
          >
            {t(projectWords?.nameKey ?? 'auth.appNameLabel', { app: appName })}
          </p>
        ) : null}
        <h1
          className={cn(
            'font-display text-xl sm:text-2xl font-semibold tracking-tight text-foreground',
            fullBleed ? 'mb-3' : 'mb-2',
          )}
          dir="auto"
        >
          {heading}
        </h1>
        {brand?.authorHandle ? (
          <p className="text-sm text-muted-foreground mb-2" data-testid="login-brand-author">
            {t('auth.byAuthor', { author: brand.authorHandle })}
          </p>
        ) : null}
        {/* A line break written in the text is kept (a project's line of two
            or three short sentences puts each on its own row), and whatever
            wraps is balanced, so one word is never left alone on the last
            row ("... invited you to connect on / Revent."). Balance ALONE
            broke the two-sentence line in the middle of a sentence. An
            invitation's one line gets the room to stand whole (the Revent
            design rework, 2026-10-18: "Ben Okafor invited you / to connect
            on Revent." broke at 300 px for no reason a reader could see). */}
        <p
          className={`text-sm text-muted-foreground leading-relaxed mx-auto whitespace-pre-line text-balance ${invited ? 'max-w-[360px]' : 'max-w-[300px]'}`}
          data-testid="login-intro"
          data-invited={invited ? '' : undefined}
          dir="auto"
        >
          {line}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {error === 'oauth_error' && t('auth.oauthError')}
          {error === 'no_code' && t('auth.noCodeError')}
          {error === 'auth_failed' && t('auth.authFailedError')}
          {error === 'access_denied' && t('auth.accessRestricted')}
        </div>
      )}

      {ref && (
        <div className="mb-4 p-3 rounded-lg bg-primary/10 border border-primary/20 text-primary text-sm flex items-center gap-2">
          <Sparkles className="h-4 w-4 shrink-0" />
          <span>{t('auth.invitedNotice')}</span>
        </div>
      )}

      {named ? (
        <div className="space-y-3" data-testid="login-app-panel">
          {guestBlock}
          <div className="space-y-2">
            {providersKnown
              ? primaryProviders.map((slug, i) => providerButton(slug, i === 0 && !guestUrl))
              : providerPlaceholders(APP_SCREEN_PRIMARY_COUNT)}
          </div>
          {hasMore ? (
            <div className="space-y-2 pt-1">
              <button
                type="button"
                aria-expanded={moreOpen}
                aria-controls={moreOpen ? moreId : undefined}
                onClick={toggleMore}
                data-testid="login-other-ways"
                className="mx-auto flex items-center gap-1 rounded px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-11"
              >
                {t('auth.otherWaysToSignIn')}
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', moreOpen && 'rotate-180')}
                  aria-hidden
                />
              </button>
              {moreOpen ? (
                <div id={moreId} data-testid="login-other-ways-panel" className="space-y-2">
                  {moreProviders.map((slug) => providerButton(slug, false))}
                  {showPasskey ? passkeyButton : null}
                  <p className="text-center text-xs text-muted-foreground">{t('auth.otherWaysHint')}</p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="card p-5 space-y-4">
          <div className="space-y-2">
            {providersKnown
              ? primaryProviders.map((slug, i) => providerButton(slug, i === 0))
              : providerPlaceholders(3)}
          </div>
          {showPasskey ? passkeyButton : null}
        </div>
      )}

      {partner ? (
        // WHO SENT THE PERSON SIGNS THE FOOT (the owner, 2026-10-04 night, on
        // the row that first stood under the heading: "should we find a better
        // place for dp x R? feels like a wall of text"). Two names and a sign,
        // in the size and the tone of the terms under it, and "<App> runs on
        // Robutler." stays where it was (the owner: "keep runs on robutler"):
        // the row says who sent the person, the sentence says what the app is
        // built on. Names are not translated; the sign is read out as "and".
        <p
          className="text-center text-xs text-muted-foreground/60 mt-5"
          data-testid="login-partner"
          aria-label={`${partner} ${t('auth.and')} Robutler`}
        >
          <span className="font-medium text-foreground/60">{partner}</span>
          {' \u00d7 '}
          <span className="font-medium text-foreground/60">Robutler</span>
        </p>
      ) : null}
      {/* THE FOOT IS ONE TONE (the owner, 2026-10-05). He asked for "less
          bright bottom small type" and chose the version with the whole foot
          at 60%. For an hour only the asides were toned down and the terms
          sentence kept its tone, because that sentence is the notice the
          acceptance of the Terms and the age declaration rest on and at 60%
          it reads 2.3 to 1 against the page (4.8 to 1 before); he saw one
          lighter half-sentence and said "only Revent runs on Robutler is
          lighter - wtf? ugly!". So an app's screen sets all of it at 60%,
          the links underlined as before; the platform's own screen keeps the
          full tone. Putting the notice back is this one class. */}
      <p
        className={cn(
          'text-center text-xs',
          named ? 'text-muted-foreground/60' : 'text-muted-foreground',
          partner ? 'mt-1.5' : 'mt-5',
        )}
      >
        {named ? (
          <>
            <span data-testid="login-runs-on">{t('auth.appRunsOn', { app: appName })}</span>{' '}
          </>
        ) : null}
        {isLoginMode ? t('auth.termsAgreementLogin') : t('auth.termsAgreement')}{' '}
        <a
          href={`${apexBase}/doc/terms-of-service`}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground transition-colors"
        >
          {t('home.footer.terms')}
        </a>{' '}
        {t('auth.and')}{' '}
        <a
          href={`${apexBase}/doc/privacy-policy`}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground transition-colors"
        >
          {t('auth.privacyPolicy')}
        </a>
        {/* Age representation (ToS §4, 18+). Part of the same sign-in-wrap:
            recordTermsAcceptance stamps acceptedTermsAt/-Version on every
            sign-in, so from CURRENT_LEGAL_VERSION 2026-07-31 that stamp IS
            the recorded 18+ self-declaration (G9 in the compliance review). */}
        {', '}{t('auth.ageDeclaration')}
      </p>

      {!onHost && !named && (
        <p className="text-center text-xs text-muted-foreground/50 mt-3">
          {t('auth.devLeadIn')}{' '}
          <a
            href="/develop"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground transition-colors"
          >
            {t('auth.devLink')}
          </a>
        </p>
      )}

      {inApp && !inAppDismissed && !guestUrl ? (
        <InAppBrowserSheet
          detection={inApp}
          href={pageHref}
          // Every provider of THIS screen but Google (the one that refuses
          // a web view), with the SAME hrefs as the buttons underneath, so a
          // host hop or an account-picker flag is honoured on the sheet
          // exactly as on the page.
          alternatives={providers
            .filter((slug) => slug !== 'google')
            .map((slug) => ({ slug, href: startUrl(slug), label: t(SIGN_IN_PROVIDER_INFO[slug].labelKey) }))}
          onProvider={(slug) => trackAuthStart(slug)}
          onDismiss={() => setInAppDismissed(true)}
        />
      ) : null}
    </div>
  );

  // ANCHORED, NOT CENTRED (2026-10-02, the visual check). "Other ways to sign
  // in" opens in place and makes the panel taller. Centred, the panel then
  // moved up by half of that under the pointer, and the spot just pressed
  // held "Continue with Discord": a second press, or a double click, started
  // a real sign-in. So the panel's top never moves when it opens. From `md`
  // up the full-bleed screen gives the panel a fixed top offset (about where
  // a centred, closed panel starts); at phone width it freezes the layout on
  // the first press (`toggleMore` above); the page and the workspace dialog
  // anchor the lean screen at the top (they read `data-named`).
  if (fullBleed && art && screen && flagship) {
    // THE FULL-BLEED SCREEN. Two columns from `md` up: the panel on the
    // page's background, and the app's colour edge to edge in the right half,
    // held in place while the panel's column scrolls. At phone width the
    // PANEL SITS AT THE BOTTOM, where a thumb is (the owner, 2026-10-03), and
    // the colour is a band that takes whatever height is left above it, never
    // less than 12rem; the panel's column is pulled up over the band's lower
    // edge by the mark's whole height (half of it until 2026-10-05, when the
    // owner asked for the mark higher and the room under it), so the mark
    // stands IN the band's fade, ringed in the page's ground like a picture
    // over a cover, and the words start where the band ends. The band is decorative (the heading and the line
    // say everything it shows), so the whole art column is hidden from
    // assistive technology. One <picture>, so a phone downloads only the phone
    // file. The page wrapper reads `data-bleed` to drop its centring and
    // padding, so the colour reaches the screen's edges.
    const cover = coverGradient(flagship.appId, screen.mark.color);
    // ART SEEN WHOLE (2026-10-04, a project's wordmark): centred, never
    // cropped, no wider than it stays sharp, on its own ground with no
    // highlight, so the file's edges do not show. At phone width its box ends
    // where the fade that carries the mark begins, which would otherwise wash
    // out its lower half.
    const whole = art === 'picture' ? (image?.whole ?? null) : null;
    // SIZED TO THE ART, NOT TO THE FILE (2026-10-04 night). A wordmark file
    // carries its own margin, and the band on a phone with the browser's bars
    // showing is about 250 px tall: fitting the FILE into what the fade
    // leaves drew the Tech Week wordmark 76 px wide. `artHeight` says how
    // much of the square file's height is art, and the file is drawn as tall
    // as puts the ART at the box's height, never wider than the box or than
    // `maxPx`; the margin it spills is the ground's own colour. Container
    // units do the sum, and a browser without them keeps the old fit.
    // ONE SHARE PER FILE (2026-10-10, LA Tech Week's band). The phone file
    // holds two lines across its middle and the desktop file three in a
    // taller block, so one number is not true of both: the phone's share put
    // on the desktop file would clip the block in a short window, and the
    // desktop's put on the phone file drew the two lines at half their size.
    // A project may give each file its own share; the desktop's applies from
    // `md` up, exactly where the <source> below serves the desktop file.
    const fitFor = (share: number, maxPx: number) => `min(100cqw, ${Math.round(100 / share)}cqh, ${maxPx}px)`;
    const shares = whole?.artHeight ?? null;
    const artFit =
      whole && shares ? fitFor(typeof shares === 'number' ? shares : shares.phone, whole.maxPx) : null;
    const artFitMd = whole && shares && typeof shares !== 'number' ? fitFor(shares.desktop, whole.maxPx) : null;
    // The inviter's circle holds the first letter of the name the line draws;
    // with no name the screen may draw, a person glyph like the other circle.
    const initial = art === 'pair' ? inviterInitial(loginApp?.invitedBy) : null;
    // A sent event's name is set in the display face, three rows at most,
    // smaller for a longer name so that a real title reads whole on a phone's
    // band. The four sizes follow the week's own titles (1,639 of them,
    // measured 2026-10-05: a quarter run to 24 letters, half from 25 to 48, a
    // quarter from 49 to 72, one in twenty longer, the longest 102, which the
    // lookup cuts at 90).
    const eventArt = art === 'plain' ? eventName : null;
    const eventLetters = eventArt ? Array.from(eventArt).length : 0;
    const eventSize = (short: number, mid: number, long: number, longest: number) =>
      `${eventLetters <= 24 ? short : eventLetters <= 48 ? mid : eventLetters <= 72 ? long : longest}px`;
    return (
      <div
        data-testid="login-screen"
        data-layout="full-bleed"
        data-screen-source={projectScreen?.source}
        data-bleed=""
        className="flex min-h-svh w-full flex-col bg-background md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]"
      >
        <div
          ref={columnRef}
          data-testid="login-column"
          className="relative z-10 flex justify-center px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-md:-mt-14 md:px-10 md:pb-10 md:pt-[max(6svh,calc(50svh_-_12rem))]"
          style={frozen ? { minHeight: frozen.column } : undefined}
        >
          {panel}
        </div>
        <div
          ref={artRef}
          aria-hidden
          data-testid="login-art"
          data-art={art === 'picture' ? (whole ? 'whole' : image?.fill ? 'fill' : 'shot') : eventArt ? 'event' : art}
          data-frozen={frozen ? '' : undefined}
          className={cn(
            'relative overflow-hidden max-md:order-first md:sticky md:top-0 md:h-svh md:self-start',
            frozen ? 'max-md:h-[var(--login-band)] max-md:flex-none' : 'max-md:min-h-48 max-md:flex-1',
          )}
          style={{
            background: whole
              ? whole.ground
              : `radial-gradient(110% 80% at 0% 0%, rgb(255 255 255 / 0.24), transparent 60%), ${cover.css}`,
            ...(frozen ? ({ '--login-band': `${frozen.band}px` } as React.CSSProperties) : {}),
          }}
        >
          {art === 'picture' && image ? (
            <div
              data-testid="login-art-window"
              className={cn(
                'absolute overflow-hidden',
                whole
                  ? 'inset-0 flex items-center justify-center px-[10%] pt-[8%] pb-[8%] [container-type:size] max-md:bottom-[min(45%,8rem)] max-md:pb-[2%]'
                  : image.fill
                    ? 'inset-0'
                    : 'left-[7%] right-[7%] top-[16%] -bottom-[12%] rounded-t-2xl shadow-2xl ring-1 ring-black/10 dark:ring-white/10 md:left-[11%] md:-right-[12%] md:top-[12%] md:rounded-tr-none',
              )}
            >
              <picture
                className={cn(
                  'block h-full w-full',
                  artFit &&
                    'shrink-0 supports-[width:1cqw]:h-[var(--login-art-fit)] supports-[width:1cqw]:w-[var(--login-art-fit)]',
                  artFitMd &&
                    'md:supports-[width:1cqw]:h-[var(--login-art-fit-md)] md:supports-[width:1cqw]:w-[var(--login-art-fit-md)]',
                )}
                style={
                  whole
                    ? ({
                        maxWidth: whole.maxPx,
                        ...(artFit ? { '--login-art-fit': artFit } : {}),
                        ...(artFitMd ? { '--login-art-fit-md': artFitMd } : {}),
                      } as React.CSSProperties)
                    : undefined
                }
              >
                <source media="(min-width: 48rem)" srcSet={image.desktop} />
                <img
                  src={image.phone}
                  alt=""
                  draggable={false}
                  data-testid="login-screen-image"
                  className={cn(
                    'block h-full w-full',
                    whole ? 'object-contain object-center' : image.fill ? 'object-cover object-center' : 'object-cover object-left-top',
                  )}
                />
              </picture>
            </div>
          ) : art === 'pair' ? (
            // TWO PEOPLE AND A LINE, drawn in white on the app's colour and, at
            // phone width, above the fade like a wordmark. The inviter is a
            // filled circle and the arriving person an open one, joined by a
            // dashed line: a connection that is offered and not yet made. The
            // letter wears the app's colour.
            <div
              data-testid="login-art-figure"
              className="absolute inset-0 flex items-center justify-center px-[10%] max-md:bottom-[min(45%,8rem)] max-md:pt-2.5"
            >
              <div className="flex items-center" data-testid="login-art-pair">
                <span
                  data-testid="login-art-inviter"
                  className="grid h-[68px] w-[68px] flex-none place-items-center rounded-full bg-white font-display text-[28px] font-semibold leading-none shadow-[0_8px_24px_rgb(0_0_0/0.18)] md:h-[116px] md:w-[116px] md:text-[46px]"
                  style={{ color: cover.to }}
                >
                  {initial ?? <UserRound className="h-[30px] w-[30px] md:h-[50px] md:w-[50px]" />}
                </span>
                <span className="w-[72px] border-t-2 border-dashed border-white/85 md:w-[132px] md:border-t-[3px]" />
                <span
                  data-testid="login-art-you"
                  className="grid h-[68px] w-[68px] flex-none place-items-center rounded-full border-2 border-dashed border-white/90 bg-white/[0.12] text-white md:h-[116px] md:w-[116px] md:border-[3px]"
                >
                  <UserRound className="h-[30px] w-[30px] md:h-[50px] md:w-[50px]" />
                </span>
              </div>
            </div>
          ) : eventArt ? (
            <div
              data-testid="login-art-figure"
              className="absolute inset-0 flex items-center justify-center px-[10%] max-md:bottom-[min(45%,8rem)] max-md:pt-2.5"
            >
              <span
                data-testid="login-art-event"
                className="line-clamp-3 text-balance text-center font-display text-[length:var(--login-event-sm)] font-bold leading-[1.08] tracking-[-0.025em] text-white md:text-[length:var(--login-event-md)]"
                style={
                  {
                    '--login-event-sm': eventSize(40, 30, 24, 20),
                    '--login-event-md': eventSize(84, 60, 46, 40),
                  } as React.CSSProperties
                }
              >
                {eventArt}
              </span>
            </div>
          ) : null}
          {/* At phone width the band fades into the page, and THE MARK STANDS IN
              THE FADE (the owner, 2026-10-05, on a build whose fade was solid
              white behind the whole mark: "on mobile the icon is not going
              into the gradient - fix"). So the fade runs evenly to the band's
              lower edge and the mark, 56 px tall and ringed in the page's
              ground, sits over its lower half. That is right wherever
              nothing is drawn under the fade: a wordmark, the two people and
              an event's name all end where it begins. A product shot or art
              that fills the band does run under it, and there the fade stays
              SOLID from 60% down, so the mark is not set on the picture's
              text (the visual pass of 2026-10-03). */}
          <div
            data-testid="login-art-fade"
            className={cn(
              'pointer-events-none absolute inset-x-0 bottom-0 h-[min(45%,8rem)] bg-gradient-to-b from-transparent to-background md:hidden',
              art === 'picture' && !whole && 'to-60%',
            )}
          />
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="login-screen"
      data-layout={named ? 'lean' : 'platform'}
      data-screen-source={named ? projectScreen?.source : undefined}
      data-named={named ? '' : undefined}
      className="w-full max-w-[350px] mx-auto"
    >
      {panel}
    </div>
  );
}
